
/**
 * Module dependencies.
 */

import { Loading } from '../loading';
import { ReactNode } from 'react';
import { color, media } from 'react-components/styles';
import { switchProp } from 'styled-tools';
import styled, { css } from 'styled-components';

/**
 * `ColorTheme` type.
 */

type ColorTheme = 'gray' | 'primary' | 'transparent';

/**
 * `Size` type.
 */

type Size = 'extraLarge' | 'large' | 'medium' | 'small';

/**
 * Export `AvatarProps` type.
 */

export type AvatarProps = {
  className?: string;
  isLoading?: boolean;
  size: Size | string;
  spinner?: {
    size?: number;
    stroke?: number;
  };
};

/**
 * `Props` type.
 */

type Props = AvatarProps & {
  children?: ReactNode;
  colorTheme: ColorTheme;
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div<{ colorTheme: ColorTheme; size: Size | string }>`
  align-items: center;
  clip-path: url(#slyk-mask);
  display: flex;
  font-size: 14px;
  font-weight: 400;
  justify-content: center;
  letter-spacing: -0.2px;
  line-height: 20px;
  overflow: hidden;
  position: relative;

  ${switchProp('colorTheme', {
    gray: css`
      background-color: ${color('gray500')};
      color: ${color('white')};
    `,
    primary: css`
      background-color: ${color('primary')};
      color: ${color('white')};
    `,
    transparent: css`
      background-color: transparent;
    `
  })}

  ${switchProp('size', {
    extraLarge: css`
      height: 72px;
      width: 72px;
    `,
    large: css`
      height: 56px;
      width: 56px;
    `,
    medium: css`
      height: 40px;
      width: 40px;
    `,
    small: css`
      height: 32px;
      width: 32px;
    `
  }, css`
    height: ${props => (props as unknown as { size: string }).size};
    width: ${props => (props as unknown as { size: string }).size};
  `)}

  ${media.min('md')`
    font-size: 16px;
    font-weight: 700;
    line-height: 22px;
  `}
`;

/**
 * Export `Avatar` component.
 */

export function Avatar(props: Props): JSX.Element {
  const { children, isLoading, spinner, ...rest } = props;

  return (
    <Wrapper {...rest}>
      {isLoading ? (
        <Loading
          {...spinner}
          active
        />
      ) : children}
    </Wrapper>
  );
}
